import React, {ReactNode} from 'react';

import {
  BaseUserAvatar,
  getUserTitle,
  getUserTooltipItemName,
} from './BaseUserAvatar';
import {
  MultipleBaseAvatar,
  SingleAvatarAdditionalProps,
} from './MultipleBaseAvatar';
import {getRoleTitle, getRoleTooltipItemName, RoleAvatar} from './RoleAvatar';
import {getTeamTitle, getTeamTooltipItemName, TeamAvatar} from './TeamAvatar';
import {UserAvatar} from './UserAvatar';

import {
  BasicRoleSerializedType,
  BasicTaskUserSerializedType,
  BasicTeamSerializedType,
} from 'sweet-types';

import type {OverlayPlacementType} from 'app/general/overlays';

interface Props {
  className?: string;
  usersAndTeamsAndRoles: Array<
    | BasicTeamSerializedType
    | BasicTaskUserSerializedType
    | BasicRoleSerializedType
  >;
  tooltipPlacement?: OverlayPlacementType;
  tooltipTitle?: string;
  tooltipListLimit?: number;
}
export function MultipleAssignedAvatar({
  usersAndTeamsAndRoles,
  className,
  tooltipPlacement,
  tooltipTitle,
  tooltipListLimit,
}: Props): JSX.Element {
  return (
    <MultipleBaseAvatar
      items={usersAndTeamsAndRoles}
      renderItemAvatar={renderAssignedItemAvatar}
      renderItemTooltipText={getItemTooltipText}
      serializeItem={serializeUserOrTeamOrRole}
      getItemId={(item) => `${item.content_type}:${item.id}`}
      className={className}
      tooltipPlacement={tooltipPlacement}
      tooltipTitle={tooltipTitle}
      tooltipListLimit={tooltipListLimit}
    />
  );
}

const serializeUserOrTeamOrRole = (
  item:
    | BasicTeamSerializedType
    | BasicTaskUserSerializedType
    | BasicRoleSerializedType
): string =>
  item.content_type === 'team'
    ? [item.name, item.id].join('|')
    : item.content_type === 'user'
    ? [item.name, item.id, item.email, item.avatar_image].join('|')
    : item.content_type === 'role'
    ? [item.name, item.id].join('|')
    : '';

const renderAssignedItemAvatar = (
  item:
    | BasicTeamSerializedType
    | BasicTaskUserSerializedType
    | BasicRoleSerializedType,
  props: SingleAvatarAdditionalProps
): JSX.Element =>
  item.content_type === 'team' ? (
    <TeamAvatar team={item} {...props} />
  ) : item.content_type === 'user' ? (
    props.multiple ? (
      <BaseUserAvatar user={item} {...props} />
    ) : (
      <UserAvatar
        user={item}
        isGuest={
          'is_guest_until' in item ? item.is_guest_until != null : undefined
        }
        {...props}
      />
    )
  ) : (
    <RoleAvatar role={item} {...props} />
  );

const getItemTooltipText = (
  item:
    | BasicTeamSerializedType
    | BasicTaskUserSerializedType
    | BasicRoleSerializedType
): ReactNode =>
  item.content_type === 'team'
    ? getTeamTooltipItemName(getTeamTitle(item))
    : item.content_type === 'user'
    ? getUserTooltipItemName(
        getUserTitle(item),
        'is_guest_until' in item ? item.is_guest_until : undefined
      )
    : item.content_type === 'role'
    ? getRoleTooltipItemName(getRoleTitle(item))
    : null;
